import { usePostHog } from 'posthog-js/react/slim';
import type { Survey } from 'posthog-js';

import { useOnboardingComplete } from '../../../hooks/api/useOnboardingComplete';
import { SurveyWizard } from '../../generic/Survey/SurveyWizard';
import { recordSurveyLastSeen } from '../surveySeenStorage';

import { ThanksStep } from './ThanksStep';
import { WelcomeStep } from './WelcomeStep';

import styles from './OnboardingSurveyFullscreen.module.less';

type Props = {
  survey: Survey;
  onClose: () => void;
};

/**
 * Fullscreen takeover for the post-signup onboarding survey. Composes
 * `SurveyWizard` with the FOSSA-branded `WelcomeStep` (preStep) and
 * `ThanksStep` (postStep). On submit, marks the user as having completed
 * onboarding server-side (authoritative gate via `users.onboardingSurveyCompletedAt`)
 * and sets the PostHog `seen_onboarding` person property for analytics.
 * `onClose` is fired by the Thanks step's Done button to dismiss the takeover.
 */
export const OnboardingSurveyFullscreen = ({ survey, onClose }: Props) => {
  const posthog = usePostHog();
  const { mutate: markOnboardingComplete } = useOnboardingComplete();

  const handleSubmit = () => {
    posthog?.setPersonProperties({ seen_onboarding: true });
    // Keeps NPS from appearing right after onboarding.
    recordSurveyLastSeen();
    markOnboardingComplete();
  };
  return (
    <div className={styles.container} data-testid="onboarding-survey-fullscreen">
      <div className={styles.frame}>
        <SurveyWizard
          survey={survey}
          preSteps={[<WelcomeStep key="welcome" />]}
          postSteps={[<ThanksStep key="thanks" onClose={onClose} />]}
          shouldShowProgressBar
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};
